import type { CycleCollectionView } from "@/services/types";
import { Badge } from "@/components/base/Badge";
import { Select, type SelectOption } from "@/components/base/Select";
import { CurrencyDisplay } from "@/components/currency/CurrencyDisplay";

interface HeaderBandProps {
  view: CycleCollectionView;
  selectedCycle: number;
  onCycleChange: (cycle: number) => void;
}

export function HeaderBand({ view, selectedCycle, onCycleChange }: HeaderBandProps) {
  const cycleOptions: SelectOption[] = view.availableCycles.map((c) => ({
    value: String(c.cycle),
    label: `Cycle ${c.cycle} — ${c.periodLabel}`,
  }));

  const stats = [
    { label: "Demand per member", value: <CurrencyDisplay amount={view.demandPerMember} hideSymbol /> },
    ...(view.commissionAmount && view.commissionAmount > 0
      ? [{ label: "Organizer commission", value: <CurrencyDisplay amount={view.commissionAmount} hideSymbol />, tone: "text-primary-700" as const }]
      : []),
    { label: "Total expected", value: <CurrencyDisplay amount={view.totalExpected} hideSymbol /> },
    { label: "Collected", value: <CurrencyDisplay amount={view.totalCollected} hideSymbol />, tone: "text-success-600" as const },
    { label: "Balance to come", value: <CurrencyDisplay amount={view.balance} hideSymbol />, tone: view.balance > 0 ? ("text-warning-700" as const) : undefined },
  ];

  return (
    <div className="rounded-lg border border-background-200 bg-background-50 p-4">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
        <div className="flex items-start gap-3">
          <div className="flex h-11 w-11 flex-none items-center justify-center rounded-md bg-secondary-100 text-secondary-700">
            <i className="ri-wallet-3-line text-xl" />
          </div>
          <div>
            <div className="flex flex-wrap items-center gap-2">
              <h2 className="text-base font-semibold text-foreground-950">{view.groupName}</h2>
              <span className="tnum text-xs text-foreground-400">{view.groupCode}</span>
              {view.isOrganizerReserved ? (
                <Badge color="accent" dot>Reserved cycle</Badge>
              ) : (
                <Badge variant="active" dot>Cycle {view.cycle}</Badge>
              )}
              {view.status === "CLOSED" ? (
                <Badge variant="draft" dot={false}>Cycle Completed</Badge>
              ) : null}
            </div>
            <p className="mt-0.5 text-sm text-foreground-500">{view.periodLabel} · monthly installment {view.monthlyInstallment.toLocaleString("en-IN")}</p>
          </div>
        </div>
        <div className="w-full lg:w-72">
          <Select
            label="Cycle"
            value={String(selectedCycle)}
            onChange={(v) => onCycleChange(Number(v))}
            options={cycleOptions}
          />
        </div>
      </div>
      <div className="mt-4 grid grid-cols-2 gap-3 border-t border-background-100 pt-4 sm:grid-cols-3 lg:grid-cols-5">
        {stats.map((s) => (
          <div key={s.label}>
            <p className="text-xs text-foreground-500">{s.label}</p>
            <p className={`tnum mt-1 text-lg font-semibold ${s.tone ?? "text-foreground-950"}`}>{s.value}</p>
          </div>
        ))}
      </div>
    </div>
  );
}